import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_LABEL,
} from './field-styles';
import { uniqueId } from './ids';

/** Testo su più righe con etichetta, aiuto ed errore (messaggio dell'invito, elenco di indirizzi). */
@Component({
  selector: 'bs-textarea',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="labelClass">
      {{ label() }}
      @if (optional()) {
        <span class="font-normal text-stone-500 dark:text-stone-400">· facoltativo</span>
      }
    </label>
    <textarea
      [id]="id"
      [rows]="rows()"
      [class]="textareaClass()"
      [value]="value()"
      [disabled]="disabled()"
      [attr.maxlength]="maxLength()"
      [attr.placeholder]="placeholder()"
      [attr.aria-invalid]="error() ? 'true' : null"
      [attr.aria-describedby]="describedBy()"
      [attr.data-testid]="testId()"
      (input)="onInput($event)"
    ></textarea>
    @if (hint(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class TextareaComponent {
  readonly label = input.required<string>();
  readonly value = model('');
  readonly rows = input(3);
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly placeholder = input<string | null>(null);
  readonly maxLength = input<number | null>(null);
  readonly optional = input(false);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);

  protected readonly id = uniqueId('bs-textarea');
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;
  protected readonly textareaClass = computed(
    () =>
      `w-full rounded-xl border bg-white px-3 py-2.5 text-base text-ink placeholder:text-stone-500 disabled:cursor-not-allowed disabled:bg-stone-100 dark:bg-stone-900 dark:text-stone-50 dark:placeholder:text-stone-400 ${this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER}`,
  );
  protected readonly describedBy = computed(
    () =>
      [this.hint() ? this.hintId : null, this.error() ? this.errorId : null]
        .filter(Boolean)
        .join(' ') || null,
  );

  protected onInput(event: Event): void {
    this.value.set((event.target as HTMLTextAreaElement).value);
  }
}
